import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ErrorBanner from './components/ErrorBanner';
import { reportFailure } from './lib/error-bus';
import { AuthProvider } from './auth/AuthProvider';
import App from './App';
import { applyTheme, initialTheme } from './lib/theme';
import { registerServiceWorker } from './lib/push';
import './styles/index.css';

// Apply theme attribute before React mounts so we don't get a flash
applyTheme(initialTheme());

// M-21 (Daniel 2026-08-04): register the push service worker. It caches
// nothing (see public/sw.js) so this cannot stale a deploy; it exists so
// installed iPhones can receive lead/reply/missed-call notifications.
registerServiceWorker();

const queryClient = new QueryClient({
  // Daniel 2026-10-14: every failed save or page load gets one shared, plain
  // banner with a reference code, instead of failing quietly. The rules for
  // what is worth a banner live in lib/error-bus.ts. A query or mutation that
  // handles its own error on screen can opt out with meta: { quiet: true }.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.quiet) return;
      reportFailure(error, { kind: 'load', hadData: query.state.data !== undefined });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.quiet) return;
      reportFailure(error, { kind: 'save' });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Root element #root not found in index.html');

/*
 * B-3 (Daniel 2026-08-12): react-router-dom 6.30.4 to 7.18.2.
 *
 * There is deliberately no `future={{...}}` prop here. On 6.30 those flags
 * were how you opted into the v7 behaviours early; on v7 the behaviours ARE
 * the default and the prop no longer exists, so passing it is a type error.
 * If this ever gets rolled back to v6, the app returns to v6 defaults, which
 * is the behaviour it shipped with for months. Nothing else in apps/web
 * changed for the upgrade.
 *
 * Which v7 behaviour changes this app was actually exposed to, so that the
 * next person does not have to work it out again:
 *
 *   v7_relativeSplatPath  no effect. It changes how relative links resolve
 *     under a MULTI-segment splat route like "dashboard/*". The only splat
 *     here is the bare "*" catch-all at the bottom of App.tsx, which is
 *     single-segment and renders <Navigate to="/">, an absolute target.
 *     Nothing resolves relative to it.
 *
 *   v7_startTransition  router state updates now go through
 *     React.useTransition rather than useState. The documented way to break
 *     on this is calling React.lazy INSIDE a component render. B-9
 *     (2026-08-17) introduced route-level code splitting, but every lazy()
 *     call in App.tsx runs once at module scope, never inside a render, so
 *     the components are stable across renders and this hazard does not
 *     apply. Both flags were switched on while still on 6.30 and verified
 *     before the version bump, which is the upgrade path React Router
 *     recommends.
 *
 *   The other four v7 flags (v7_fetcherPersist, v7_normalizeFormMethod,
 *     v7_partialHydration, v7_skipActionErrorRevalidation) only apply to a
 *     data router built with createBrowserRouter and rendered through
 *     RouterProvider. This app declares routes as JSX inside <BrowserRouter>
 *     and has no loaders, actions or fetchers, so none of them apply.
 *
 * Every route is checked by scripts/verify-routes.mjs, which renders the
 * whole table through the real router and asserts each path still resolves
 * to the same component. Run it after touching anything in App.tsx.
 */
createRoot(root).render(
  <StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
          <ErrorBanner />
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </StrictMode>,
);
